import { useState } from "react";
import { Spin } from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PrinterOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { LunchMenu } from "../../../../api/lunchMenus";
import {
  useDeleteLunchMenu,
  useFinishLunchMenu,
  useLunchMenu,
} from "../../../../hooks/useLunchMenus";
import { useViewport } from "../../../../hooks/useViewport";
import { getApiErrorMessage } from "../../../../utils/translations";
import { ConfirmActionModal } from "../../components/tables/ConfirmActionModal";
import { useOrderingMessage } from "../../components/useOrderingMessage";
import { lunchPeriodText, lunchScheduleText } from "../lunchDraft";
import { LunchMenuPrint } from "../print/LunchMenuPrint";
import { printReportDocument } from "../../components/tables/reports/reportDocumentPrint";
import { LunchActionButton } from "./LunchActionButton";
import { LunchMenuItemsTable } from "./LunchMenuItemsTable";
import { LunchStatusPill } from "./LunchStatusPill";

interface LunchMenuDetailProps {
  menuId: number;
  onEdit: (menu: LunchMenu) => void;
  onCopy: (menu: LunchMenu) => void;
  onRemoved: () => void;
}

type PendingAction = "finish" | "delete" | null;

/** Детайлът на избраното обедно меню: график, действия и продуктите му. */
export const LunchMenuDetail = ({
  menuId,
  onEdit,
  onCopy,
  onRemoved,
}: LunchMenuDetailProps) => {
  const { t } = useTranslation();
  const { isNarrow } = useViewport();
  const message = useOrderingMessage();
  const { data: menu, isLoading, isError } = useLunchMenu(menuId);
  const finishMutation = useFinishLunchMenu();
  const deleteMutation = useDeleteLunchMenu();
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  if (isLoading)
    return (
      <div className="grid flex-1 place-items-center py-16">
        <Spin />
      </div>
    );
  if (isError || !menu)
    return (
      <p className="m-0 py-10 text-center text-pos-soft">
        {t("lunchMenu.loadError")}
      </p>
    );

  const confirmPendingAction = async () => {
    const action = pendingAction;
    setPendingAction(null);
    try {
      if (action === "finish") {
        await finishMutation.mutateAsync(menu.id);
        message.success(t("lunchMenu.finished"));
      } else if (action === "delete") {
        await deleteMutation.mutateAsync(menu.id);
        message.success(t("lunchMenu.deleted"));
        onRemoved();
      }
    } catch (error) {
      message.error(getApiErrorMessage(error, t("common.error")));
    }
  };

  const period = lunchPeriodText(t, menu);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h2
            className={`m-0 font-extrabold leading-[1.2] text-pos-ink ${
              isNarrow ? "text-[20px]" : "text-[26px]"
            }`}
          >
            {menu.name}
          </h2>
          <LunchStatusPill menu={menu} />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-pos-soft2">
          <span className="inline-flex items-center gap-1.5">
            <CalendarOutlined />
            <span className="font-pos-mono">{lunchScheduleText(t, menu)}</span>
          </span>
          {period && (
            <span className="inline-flex items-center gap-1.5">
              <ClockCircleOutlined />
              <span className="font-pos-mono">{period}</span>
            </span>
          )}
          {menu.createdByName && (
            <span>
              {t("lunchMenu.createdBy", { name: menu.createdByName })}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <LunchActionButton
          icon={<EditOutlined />}
          label={t("lunchMenu.edit")}
          onClick={() => onEdit(menu)}
        />
        <LunchActionButton
          icon={<CopyOutlined />}
          label={t("lunchMenu.copy")}
          onClick={() => onCopy(menu)}
        />
        <LunchActionButton
          icon={<PrinterOutlined />}
          label={t("lunchMenu.print")}
          onClick={printReportDocument}
        />
        {menu.status !== "past" && (
          <LunchActionButton
            icon={<StopOutlined />}
            label={t("lunchMenu.finishNow")}
            onClick={() => setPendingAction("finish")}
          />
        )}
        <span className="flex-1" />
        <LunchActionButton
          danger
          icon={<DeleteOutlined />}
          label={t("lunchMenu.delete")}
          onClick={() => setPendingAction("delete")}
        />
      </div>

      <LunchMenuItemsTable items={menu.items} />
      <p className="m-0 flex items-center gap-2 text-[13px] text-pos-soft2">
        <span
          aria-hidden
          className="h-2 w-2 shrink-0 rounded-full bg-pos-accent"
        />
        {t("lunchMenu.changedSizeHint")}
      </p>

      <LunchMenuPrint menu={menu} />

      <ConfirmActionModal
        open={pendingAction != null}
        title={t(
          pendingAction === "delete"
            ? "lunchMenu.confirmDeleteTitle"
            : "lunchMenu.confirmFinishTitle",
        )}
        message={t(
          pendingAction === "delete"
            ? "lunchMenu.confirmDeleteMessage"
            : "lunchMenu.confirmFinishMessage",
          { name: menu.name },
        )}
        confirmLabel={t(
          pendingAction === "delete"
            ? "lunchMenu.delete"
            : "lunchMenu.confirmFinish",
        )}
        icon={
          pendingAction === "delete" ? <DeleteOutlined /> : <StopOutlined />
        }
        onCancel={() => setPendingAction(null)}
        onConfirm={confirmPendingAction}
      />
    </div>
  );
};
